import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { isLeasedCategory } from '../../../../utils/helpers';
import { AgencyFilterResultsBlock } from '../AgencyFilterResultsBlock';
import { AgencyFilterResultsProps, AgencyFilterCategory } from './types';
import useScreenSize from '../../../../hooks/useScreenSize';
import {
  AGENCY_FILTER_CATEGORY_ALL_ID,
  AGENCY_PAGE_DESKTOP_CARDS_LIMIT,
  DEFAULT_DESKTOP_CARDS_LIMIT
} from '../../../../utils/constants/agencyPage';
import {
  buildAgencyUrl,
  getAgencyFilterCardsLimit,
  resolveAgencyBlockParams,
  resolveHrefLeasedCategory
} from '../../../../utils/helpers/agencyPage';
import { useClient } from '../../../../hooks/useClient';
import { AgencyFilterResultsWrapper } from './styled';

export const AgencyFilterResults: React.FC<AgencyFilterResultsProps> = ({
  agencyId,
  categories,
  activeCategoryId,
  activeLeasedCategory,
  isCurrentItemPage,
  onClickItem
}) => {
  const { t } = useTranslation();
  const { isSmall, isLarge } = useScreenSize();
  const isClient = useClient();
  const router = useRouter();

  const getTitle = (category: AgencyFilterCategory) => {
    if (
      activeCategoryId === AGENCY_FILTER_CATEGORY_ALL_ID &&
      isLeasedCategory(category.id)
    ) {
      return t(`pages.agency.${category.id}`);
    }
    if (isLeasedCategory(activeCategoryId)) {
      return t(`pages.agency.filter.${activeCategoryId}`, {
        id: t(`pages.agency.filter.id_${category.id}`)
      });
    }
    if (isLeasedCategory(category.id)) {
      return t(`pages.agency.filter.${category.id}`, {
        id: t(`pages.agency.filter.id_${activeCategoryId}`)
      });
    }
    return category.title ?? '';
  };

  const desktopCardsLimit = isCurrentItemPage
    ? DEFAULT_DESKTOP_CARDS_LIMIT
    : AGENCY_PAGE_DESKTOP_CARDS_LIMIT;

  const baseLimit = useMemo(
    () =>
      getAgencyFilterCardsLimit(isLarge, isSmall, isClient, desktopCardsLimit),
    [isClient, isLarge, isSmall, desktopCardsLimit]
  );

  let renderedBlockIndex = 0;

  return (
    <AgencyFilterResultsWrapper $isCurrentItemPage={isCurrentItemPage}>
      {categories.map(category => {
        const blockParams = resolveAgencyBlockParams(
          category,
          activeCategoryId,
          activeLeasedCategory,
          baseLimit
        );
        if (!blockParams) return null;

        const { isLeased, limit, actualCategory, activeId } = blockParams;

        const hrefLeasedCategory = resolveHrefLeasedCategory(
          activeId,
          activeCategoryId,
          activeLeasedCategory
        );

        const showAllHref = isCurrentItemPage
          ? undefined
          : buildAgencyUrl({
              agencySlug: router.query.agency as string,
              clickedId: actualCategory.id,
              activeLeasedCategory: hrefLeasedCategory,
              categories
            });

        renderedBlockIndex += 1;

        return (
          <AgencyFilterResultsBlock
            key={category.id}
            title={getTitle(category)}
            category={actualCategory}
            agencyId={agencyId}
            activeCategoryId={activeId}
            limit={limit}
            isLeased={isLeased}
            onClickItem={onClickItem}
            cardsLimit={baseLimit}
            isCurrentItemPage={isCurrentItemPage}
            activeLeasedCategory={activeLeasedCategory}
            showAllHref={showAllHref}
            blockIndex={renderedBlockIndex}
          />
        );
      })}
    </AgencyFilterResultsWrapper>
  );
};
